// ============================================================
// numberField - the arithmetic behind <NumberInput>: reading what was
// typed, clamping it, and deciding what an abandoned edit commits.
// ============================================================

/** A field's bounds. With no `min`, the floor is 0 unless negatives are allowed. */
export interface NumberBounds {
  min?: number;
  max?: number;
  allowNegative?: boolean;
  /** Accept fractions; otherwise the text is read as a whole number. */
  float?: boolean;
}

/** The number in `raw` - NaN when there is none. */
export function parseNumber(raw: string, float: boolean | undefined): number {
  return float ? parseFloat(raw) : parseInt(raw, 10);
}

export function clampNumber(n: number, { min, max, allowNegative }: NumberBounds): number {
  const lo = min ?? (allowNegative ? -Infinity : 0);
  const hi = max ?? Infinity;
  return Math.max(lo, Math.min(hi, n));
}

/** Empty, a lone minus, and (for fractions) a trailing point are all halfway through typing - nothing to emit yet. */
export function isPartialNumber(raw: string, float: boolean | undefined): boolean {
  return raw === '' || raw === '-' || (!!float && /[.]$/.test(raw));
}

/** What a field commits when editing ends: its text clamped, or `min` (else 0) when the text is not a number. */
export function committedNumber(text: string, bounds: NumberBounds): number {
  const n = parseNumber(text, bounds.float);
  return Number.isFinite(n) ? clampNumber(n, bounds) : clampNumber(bounds.min ?? 0, bounds);
}
